<template>
  <div class="bridge-interface">
    <v-card class="bridge-card">
      <v-card-title class="bridge-title">
        <v-icon color="primary" class="mr-2">mdi-bridge</v-icon>
        Bridge Multi-Chain
      </v-card-title>
      
      <v-card-text>
        <!-- Seleção de redes para o bridge -->
        <div class="network-selector-container mb-6">
          <div class="network-from">
            <span class="label mb-2 d-block">De</span>
            
            <v-menu :close-on-content-click="false" location="bottom">
              <template v-slot:activator="{ props }">
                <v-btn
                  variant="outlined"
                  v-bind="props"
                  height="80"
                  width="120"
                  class="network-select-btn"
                >
                  <div class="d-flex flex-column align-center" v-if="sourceNetwork">
                    <v-avatar size="36" :color="sourceNetwork.color" class="mb-2">
                      <span class="text-h6">{{ sourceNetwork.icon }}</span>
                    </v-avatar>
                    <span>{{ sourceNetwork.name }}</span>
                  </div>
                  <div v-else>
                    Selecione Rede
                  </div>
                </v-btn>
              </template>
              
              <v-card min-width="280" class="network-selector-menu">
                <v-card-title>Selecione Rede de Origem</v-card-title>
                <v-card-text>
                  <v-list class="network-list">
                    <v-list-item
                      v-for="network in networks"
                      :key="network.id"
                      @click="selectSourceNetwork(network)"
                      :active="sourceNetwork?.id === network.id"
                    >
                      <template v-slot:prepend>
                        <v-avatar size="36" :color="network.color">
                          <span class="text-h6">{{ network.icon }}</span>
                        </v-avatar>
                      </template>
                      <v-list-item-title>{{ network.name }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-menu>
          </div>
          
          <!-- Ícone de direção -->
          <div class="direction-icon">
            <div class="bridge-animation">
              <v-icon size="x-large" color="primary">
                mdi-arrow-right
              </v-icon>
            </div>
          </div>
          
          <div class="network-to">
            <span class="label mb-2 d-block">Para</span>
            
            <v-menu :close-on-content-click="false" location="bottom">
              <template v-slot:activator="{ props }">
                <v-btn
                  variant="outlined"
                  v-bind="props"
                  height="80"
                  width="120"
                  class="network-select-btn"
                >
                  <div class="d-flex flex-column align-center" v-if="targetNetwork">
                    <v-avatar size="36" :color="targetNetwork.color" class="mb-2">
                      <span class="text-h6">{{ targetNetwork.icon }}</span>
                    </v-avatar>
                    <span>{{ targetNetwork.name }}</span>
                  </div>
                  <div v-else>
                    Selecione Rede
                  </div>
                </v-btn>
              </template>
              
              <v-card min-width="280" class="network-selector-menu">
                <v-card-title>Selecione Rede de Destino</v-card-title>
                <v-card-text>
                  <v-list class="network-list">
                    <v-list-item
                      v-for="network in networks"
                      :key="network.id"
                      @click="selectTargetNetwork(network)"
                      :active="targetNetwork?.id === network.id"
                      :disabled="sourceNetwork?.id === network.id"
                    >
                      <template v-slot:prepend>
                        <v-avatar size="36" :color="network.color">
                          <span class="text-h6">{{ network.icon }}</span>
                        </v-avatar>
                      </template>
                      <v-list-item-title>{{ network.name }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-menu>
          </div>
        </div>
        
        <!-- Seleção e quantidade de token -->
        <div v-if="sourceNetwork && targetNetwork" class="token-selection mt-6">
          <div class="token-selection-header mb-4">
            <h3 class="text-h6">Selecione Token para Bridge</h3>
          </div>
          
          <!-- Lista de tokens disponíveis para bridge -->
          <div class="token-options mb-6">
            <div
              v-for="token in getAvailableTokens(sourceNetwork.id)"
              :key="token.address"
              class="token-option"
              :class="{ 'token-selected': selectedToken?.address === token.address }"
              @click="selectToken(token)"
            >
              <v-avatar size="32" class="token-avatar mr-2">
                <v-img :src="token.logo" alt="Token Logo"></v-img>
              </v-avatar>
              <div class="token-details">
                <div class="d-flex align-center">
                  <span class="token-symbol">{{ token.symbol }}</span>
                  
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon
                        v-bind="props"
                        size="small"
                        color="info"
                        class="ml-1"
                      >
                        mdi-information-outline
                      </v-icon>
                    </template>
                    Token {{ token.name }} na rede {{ sourceNetwork.name }}
                  </v-tooltip>
                </div>
                <div class="token-balance">
                  Saldo: {{ formatTokenAmount(token.balance, token.decimals) }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- Input de quantidade -->
          <div v-if="selectedToken" class="amount-container mb-6">
            <label class="mb-2 d-block">Quantidade a transferir</label>
            <div class="d-flex align-center">
              <v-text-field
                v-model="amount"
                variant="outlined"
                type="number"
                min="0"
                step="0.000001"
                class="mr-2"
                :suffix="selectedToken.symbol"
                @input="calculateBridgeFee"
              ></v-text-field>
              
              <v-btn
                color="secondary"
                variant="text"
                @click="setMaxAmount"
              >
                MAX
              </v-btn>
            </div>
          </div>
          
          <!-- Detalhes da operação -->
          <v-card
            v-if="selectedToken && amount && parseFloat(amount) > 0"
            class="bridge-details mb-6"
            variant="outlined"
          >
            <v-card-text>
              <div class="d-flex justify-space-between mb-3">
                <span class="text-body-2">Você enviará</span>
                <span class="font-weight-medium">
                  {{ amount }} {{ selectedToken.symbol }} na rede {{ sourceNetwork.name }}
                </span>
              </div>
              
              <div class="d-flex justify-space-between mb-3">
                <span class="text-body-2">Você receberá</span>
                <span class="font-weight-medium">
                  {{ calculateReceivedAmount() }} {{ selectedToken.symbol }} na rede {{ targetNetwork.name }}
                </span>
              </div>
              
              <div class="d-flex justify-space-between mb-3">
                <span class="text-body-2">Taxa de Bridge</span>
                <span class="text-body-2">
                  {{ bridgeFee }} {{ selectedToken.symbol }} ({{ bridgeFeePercentage }}%)
                </span>
              </div>
              
              <div class="d-flex justify-space-between">
                <span class="text-body-2">Tempo estimado</span>
                <span class="text-body-2">
                  {{ estimatedTime }} minutos
                </span>
              </div>
            </v-card-text>
          </v-card>
          
          <!-- Status do bridge (visível apenas quando em progresso) -->
          <div v-if="bridgeInProgress" class="bridge-status mb-6">
            <v-alert type="info" variant="tonal">
              <div class="d-flex flex-column">
                <div class="text-subtitle-1 mb-2">Bridge em progresso</div>
                <div class="text-body-2 mb-3">Transferindo {{ amount }} {{ selectedToken.symbol }} de {{ sourceNetwork.name }} para {{ targetNetwork.name }}</div>
                
                <v-progress-linear
                  indeterminate
                  color="primary"
                  height="6"
                  class="mb-2"
                ></v-progress-linear>
                
                <div class="bridge-steps mt-4">
                  <div class="bridge-step completed">
                    <v-icon color="success" size="small">mdi-check-circle</v-icon>
                    <span class="ml-2">Transação iniciada</span>
                  </div>
                  <div class="bridge-step active">
                    <v-progress-circular
                      indeterminate
                      size="20"
                      width="2"
                      color="primary"
                    ></v-progress-circular>
                    <span class="ml-2">Confirmação na rede de origem</span>
                  </div>
                  <div class="bridge-step">
                    <v-icon color="disabled" size="small">mdi-circle-outline</v-icon>
                    <span class="ml-2">Processando bridge</span>
                  </div>
                  <div class="bridge-step">
                    <v-icon color="disabled" size="small">mdi-circle-outline</v-icon>
                    <span class="ml-2">Confirmação na rede de destino</span>
                  </div>
                </div>
              </div>
            </v-alert>
          </div>
          
          <!-- Botão de iniciar bridge -->
          <v-btn
            block
            color="primary"
            size="large"
            :disabled="!canBridge"
            @click="initiateBridge"
          >
            {{ bridgeButtonText }}
          </v-btn>
        </div>
        
        <!-- Estado vazio - nenhuma rede selecionada -->
        <div v-else class="empty-state text-center py-8">
          <v-avatar size="80" color="primary" class="mb-4">
            <v-icon size="48" color="white">mdi-bridge</v-icon>
          </v-avatar>
          <h3 class="text-h6 mb-2">Bridge Multi-Chain</h3>
          <p class="text-body-2">
            Selecione as redes de origem e destino para transferir seus tokens entre blockchains.
          </p>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { useDexStore } from '@/stores/dex'

export default {
  name: 'BridgeInterface',
  
  data() {
    return {
      sourceNetwork: null,
      targetNetwork: null,
      selectedToken: null,
      amount: '',
      bridgeFee: '0',
      bridgeFeePercentage: 0.3,
      estimatedTime: '5-15',
      bridgeInProgress: false,
      networks: [
        {
          id: 'quarnx',
          name: 'QuarnX',
          icon: 'Q',
          color: 'primary',
          chainId: '1'
        },
        {
          id: 'ethereum',
          name: 'Ethereum',
          icon: 'E',
          color: '#627EEA',
          chainId: '1'
        },
        {
          id: 'bsc',
          name: 'BSC',
          icon: 'B',
          color: '#F3BA2F',
          chainId: '56'
        },
        {
          id: 'polkadot',
          name: 'Polkadot',
          icon: 'P',
          color: '#E6007A',
          chainId: '0'
        }
      ],
      // Bridge em desenvolvimento - dados carregados da API quando disponível
      tokensData: {}
    }
  },
  
  computed: {
    dexStore() {
      return useDexStore()
    },
    
    canBridge() {
      return (
        this.sourceNetwork && 
        this.targetNetwork && 
        this.selectedToken && 
        this.amount && 
        parseFloat(this.amount) > 0 &&
        this.validateBalance()
      )
    },
    
    bridgeButtonText() {
      if (!this.sourceNetwork || !this.targetNetwork) {
        return 'Bridge em desenvolvimento'
      } else if (!this.selectedToken) {
        return 'Selecione token'
      } else if (!this.amount || parseFloat(this.amount) <= 0) {
        return 'Digite um valor'
      } else if (!this.validateBalance()) {
        return 'Saldo insuficiente'
      } else {
        return 'Iniciar Bridge'
      }
    }
  },
  
  methods: {
    selectSourceNetwork(network) {
      this.sourceNetwork = network
      this.selectedToken = null
      this.amount = ''
      
      // Se a rede de destino for igual à de origem, limpar rede de destino
      if (this.targetNetwork?.id === network.id) {
        this.targetNetwork = null
      }
    },
    
    selectTargetNetwork(network) {
      this.targetNetwork = network
    },
    
    getAvailableTokens(networkId) {
      if (!networkId || !this.tokensData[networkId]) return []
      return this.tokensData[networkId]
    },
    
    selectToken(token) {
      this.selectedToken = token
      this.amount = ''
      this.calculateBridgeFee()
    },
    
    setMaxAmount() {
      if (!this.selectedToken) return
      
      // Converter o saldo para o formato decimal
      const balance = this.formatTokenAmount(this.selectedToken.balance, this.selectedToken.decimals)
      this.amount = balance
      this.calculateBridgeFee()
    },
    
    calculateBridgeFee() {
      if (!this.amount || !this.selectedToken) {
        this.bridgeFee = '0'
        return
      }
      
      const fee = parseFloat(this.amount) * (this.bridgeFeePercentage / 100)
      this.bridgeFee = fee.toFixed(6)
    },
    
    calculateReceivedAmount() {
      if (!this.amount || !this.bridgeFee) return '0'
      
      const received = parseFloat(this.amount) - parseFloat(this.bridgeFee)
      return received.toFixed(6)
    },
    
    validateBalance() {
      if (!this.selectedToken || !this.amount) return false
      
      const balanceDecimal = parseFloat(this.formatTokenAmount(this.selectedToken.balance, this.selectedToken.decimals))
      return parseFloat(this.amount) <= balanceDecimal
    },
    
    formatTokenAmount(amount, decimals) {
      if (!amount) return '0'
      return (parseFloat(amount) / Math.pow(10, decimals)).toString()
    },
    
    initiateBridge() {
      // Bridge em desenvolvimento - funcionalidade cross-chain ainda não disponível
      alert('Bridge em desenvolvimento. A funcionalidade cross-chain será implementada em breve.')
    }
  },
  
  mounted() {
    // Definir valores padrão
    this.sourceNetwork = this.networks.find(n => n.id === 'quarnx')
    this.targetNetwork = this.networks.find(n => n.id === 'ethereum')
  }
}
</script>

<style scoped>
.bridge-card {
  max-width: 600px;
  margin: 0 auto;
  border-radius: 16px;
}

.bridge-title {
  font-size: 1.25rem;
  font-weight: 600;
}

.network-selector-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.direction-icon {
  display: flex;
  align-items: center;
  position: relative;
  padding: 0 20px;
}

.bridge-animation {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.network-select-btn {
  border-radius: 12px;
}

.token-options {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}

.token-option {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}

.token-option:hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
  transform: translateY(-2px);
}

.token-selected {
  border-color: var(--v-theme-primary);
  background-color: rgba(var(--v-theme-primary), 0.1);
}

.token-details {
  flex: 1;
}

.token-symbol {
  font-weight: 600;
}

.token-balance {
  font-size: 0.85rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.bridge-details {
  background-color: rgba(var(--v-theme-surface-variant), 0.08);
}

.bridge-steps {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bridge-step {
  display: flex;
  align-items: center;
  padding: 4px 0;
}

.bridge-step.completed {
  color: var(--v-theme-success);
}

.bridge-step.active {
  color: var(--v-theme-primary);
}
</style>
